import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { leaveServices } from '../../services/leaveServices';
import { teamServices } from '../../services/teamServices';
import { notificationTrackerService } from '../../services/notificationTrackerService';
import { useSnackbar } from '../../utils/useSnackbar';
import LeaveActionDialog from '../../utils/LeaveActionDialog';
import TableSkeleton from '../common/TableSkeleton';
import PageHeader from '../ui/PageHeader';
import '../../css/Button.css';
import '../../css/Leave.css';
import { LEAVE_STATUS } from '../../constants/leaveStatus';
import { LEAVE_TYPE, LEAVE_TYPE_LABELS, isHalfDay } from '../../constants/leaveType';

const APPROVAL_FILTER_OPTIONS = [
  { label: 'Leave Requests', value: LEAVE_STATUS.PENDING },
  { label: 'Cancellation Requests', value: LEAVE_STATUS.CANCELLATION_REQUESTED },
];

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
};

const statusColor = (status) => {
  switch (status) {
    case 'APPROVED': return 'success';
    case 'TL_APPROVED': return 'info';
    case 'REJECTED': return 'error';
    case 'CANCELLED': return 'default';
    case 'CANCELLATION_REQUESTED': return 'warning';
    default: return 'warning';
  }
};

/** Convert ISO LocalDateTime (yyyy-MM-ddT...) or yyyy-MM-dd → dd/MM/yyyy */
const toDDMMYYYY = (val) => {
  if (!val) return '';
  const datePart = val.split('T')[0]; // strip time if present
  return datePart.split('-').reverse().join('/');
};

const LeaveApproval = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const userRoles = useMemo(() => userData?.roles ?? [], [userData]);
  const userId = userData?.id;
  const actionByName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
  const navigate = useNavigate();

  const [teamOwnerIds, setTeamOwnerIds] = useState([]);
  const [ownerIdsLoaded, setOwnerIdsLoaded] = useState(false);

  useEffect(() => {
    teamServices.getAllOwnerIdsList()
      .then((data) => {
        if (Array.isArray(data)) {
          setTeamOwnerIds(data);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch team owners in LeaveApproval', err);
      })
      .finally(() => {
        setOwnerIdsLoaded(true);
      });
  }, []);

  const isAdmin = userRoles.includes('ADMIN') || userRoles.includes('SUPER_ADMIN');
  const isTeamLeader = userId && teamOwnerIds.some(id => Number(id) === Number(userId));
  const isMainTeamLeader = isAdmin || isTeamLeader;

  const canAccess = useMemo(() => {
    if (!ownerIdsLoaded) return true;
    return isMainTeamLeader;
  }, [ownerIdsLoaded, isMainTeamLeader]);

  // ── Server-side state ──────────────────────────────────────────────────
  const [leaves, setLeaves] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const [actionByComment, setActionByComment] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState(LEAVE_STATUS.PENDING);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);
  const isSearchFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // ── Debounce search ────────────────────────────────────────────────────
  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const val = search.trim();
      if (val === '' || val.length > 2) {
        setDebouncedSearch(val);
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch pending leaves ───────────────────────────────────────────────
  const fetchPending = async () => {
    if (!userId || !canAccess) return;
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'createdAt';
      const sortDir = sortModel?.[0]?.sort || 'desc';
      const data = await leaveServices.getAllLeaves(
        userId,
        userRoles,
        requestTypeFilter,
        '', // startDate
        '', // endDate
        paginationModel.page,
        paginationModel.pageSize,
        debouncedSearch,
        sortField,
        sortDir,
      );
      setLeaves(data?.content ?? []);
      setRowCount(data?.totalElements ?? 0);
    } catch (err) {
      if (err?.response?.status === 404) {
        setLeaves([]);
        setRowCount(0);
      } else {
        const msg = err?.response?.data?.message || err?.response?.data || 'Failed to load pending leaves.';
        showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
        setLeaves([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userRoles, canAccess, requestTypeFilter, paginationModel, debouncedSearch, sortModel]);

  const handleRefresh = () => {
    let changed = false;
    if (search !== '') { setSearch(''); changed = true; }
    if (debouncedSearch !== '') { setDebouncedSearch(''); changed = true; }
    if (paginationModel.page !== 0) { setPaginationModel(prev => ({ ...prev, page: 0 })); changed = true; }

    if (!changed) {
      fetchPending();
    }
  };

  // ── Sort handler ───────────────────────────────────────────────────────
  const handleSortModelChange = (newModel) => {
    if (isSortFirstRender.current) {
      isSortFirstRender.current = false;
      if (!newModel?.length) return;
    }
    const next = newModel?.[0];
    const current = sortModel?.[0];
    if (next?.field === current?.field && next?.sort === current?.sort) return;

    if (!newModel?.length && sortModel.length > 0) {
      const currentField = sortModel[0].field;
      const isColumnDefined = columns.some((c) => c.field === currentField);
      if (!isColumnDefined) {
        return;
      }
    }

    setSortModel(newModel);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  // ── Confirm dialog helpers ─────────────────────────────────────────────
  const openConfirm = (leaveId, status, row) => {
    setActionByComment('');
    setPendingAction({ leaveId, status, row });
  };

  const closeConfirm = () => {
    setActionByComment('');
    setPendingAction(null);
  };

  const handleConfirmedAction = async () => {
    if (!pendingAction) return;
    if (!actionByComment || !actionByComment.trim()) {
      showSnackbar('Comment is required.', 'error');
      return;
    }
    const { leaveId, status, row } = pendingAction;
    const commentToSend = actionByComment.trim();
    setPendingAction(null);
    setActionByComment('');
    try {
      setActionLoading((prev) => ({ ...prev, [leaveId]: status }));
      await leaveServices.updateLeaveStatus(leaveId, status, userId, actionByName, commentToSend);

      const isCancellation = row?.status === LEAVE_STATUS.CANCELLATION_REQUESTED;
      let successMsg = `Leave ${status === 'APPROVED' ? 'approved' : 'rejected'} successfully.`;
      if (isCancellation) {
        successMsg = status === 'APPROVED'
          ? 'Leave cancellation approved successfully.'
          : 'Leave cancellation rejected successfully.';
      } else if (status === 'APPROVED' && row?.isAtl && row?.status === LEAVE_STATUS.PENDING) {
        successMsg = 'Leave approved by Team Leader. Forwarded for Admin final approval.';
      }
      showSnackbar(successMsg, 'success');

      await fetchPending();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to update leave status.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[leaveId];
        return next;
      });
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        field: 'userName',
        headerName: 'Applicant',
        flex: 1,
        renderCell: (params) => (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title={params.value ?? ''}>
              <span className="leave-ellipsis">{params.value ?? '—'}</span>
            </Tooltip>
          </div>
        ),
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 1,
        renderCell: (params) => (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {fmtDate(params.value)}
          </div>
        ),
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        flex: 1,
        renderCell: (params) => (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {fmtDate(params.value)}
          </div>
        ),
      },
      {
        field: 'leaveType',
        headerName: 'Type',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const type = params.value || LEAVE_TYPE.FULL_DAY;
          const half = isHalfDay(type);
          return (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Chip
                label={LEAVE_TYPE_LABELS[type] ?? type}
                size="small"
                className={`leave-type-chip ${half ? 'leave-type-chip--half' : 'leave-type-chip--full'}`}
              />
            </div>
          );
        },
      },
      {
        field: 'reason',
        headerName: requestTypeFilter === LEAVE_STATUS.CANCELLATION_REQUESTED ? 'Cancel Reason' : 'Reason',
        flex: 1.5,
        sortable: false,
        renderCell: (params) => {
          const isCancellation = params.row.status === LEAVE_STATUS.CANCELLATION_REQUESTED;
          const displayReason = isCancellation && params.row.cancellationReason
            ? params.row.cancellationReason
            : (params.value ?? '—');
          const fullTooltip = isCancellation
            ? `Cancellation Reason: ${params.row.cancellationReason || '—'}\nLeave Reason: ${params.row.reason || '—'}`
            : (params.value ?? '');
          return (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Tooltip title={fullTooltip}>
                <span className="leave-ellipsis">
                  {displayReason}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        renderCell: (params) => {
          const isCancellation = params.value === LEAVE_STATUS.CANCELLATION_REQUESTED;
          const isTlApproved = params.value === LEAVE_STATUS.TL_APPROVED;
          const label = isCancellation
            ? 'Cancel Req.'
            : isTlApproved
            ? 'TL Approved'
            : (params.value ?? 'PENDING');

          const tooltipText = isTlApproved && params.row.tlActionByName
            ? `Approved by TL: ${params.row.tlActionByName}${params.row.tlActionByComment ? `\nNote: ${params.row.tlActionByComment}` : ''}`
            : '';

          return (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Tooltip title={tooltipText}>
                <Chip
                  label={label}
                  color={statusColor(params.value)}
                  size="small"
                  sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}
                />
              </Tooltip>
            </div>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Action',
        width: 120,
        minWidth: 110,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const id = params.row.id;
          const busy = Boolean(actionLoading[id]);

          const isAtlPending = params.row.isAtl && params.row.status === LEAVE_STATUS.PENDING;
          const isTlApproved = params.row.status === LEAVE_STATUS.TL_APPROVED;

          let canApprove = true;
          let approveTooltip = 'Approve';

          if (isAtlPending) {
            if (!isTeamLeader && isAdmin) {
              canApprove = false;
              approveTooltip = 'Awaiting Team Leader approval first';
            } else {
              approveTooltip = 'Approve (TL Approval)';
            }
          } else if (isTlApproved) {
            if (!isAdmin) {
              canApprove = false;
              approveTooltip = 'Awaiting Admin final approval';
            } else {
              approveTooltip = 'Approve (Final Approval)';
            }
          }

          return (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
              <Tooltip title={approveTooltip}>
                <span>
                  <IconButton
                    color="success"
                    size="small"
                    className="icon-button"
                    disabled={busy || !canApprove}
                    onClick={() => openConfirm(id, 'APPROVED', params.row)}
                  >
                    <CheckCircleRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject">
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    className="icon-button"
                    disabled={busy}
                    onClick={() => openConfirm(id, 'REJECTED', params.row)}
                  >
                    <CancelRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [actionLoading, requestTypeFilter, isAdmin, isTeamLeader]
  );

  // ── Access guard ───────────────────────────────────────────────────────
  if (!canAccess) {
    return (
      <div className="leave-page-container">
        <PageHeader
          title="Leave Approval"
          left={
            <IconButton onClick={() => navigate(-1)} size="medium">
              <ArrowBackIcon />
            </IconButton>
          }
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <Typography color="text.secondary">
            You do not have permission to view this page.
          </Typography>
        </Box>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────
  const renderEmptyState = () => (
    <div className="leave-empty-state">
      <div className="leave-empty-icon-wrap">
        <HourglassEmptyRoundedIcon className="leave-empty-icon" />
      </div>
      <Typography variant="h6" className="leave-empty-title">
        {requestTypeFilter === LEAVE_STATUS.CANCELLATION_REQUESTED
          ? 'No Pending Cancellation Requests'
          : 'No Pending Leave Requests'}
      </Typography>
      <Typography className="leave-empty-subtitle">
        {requestTypeFilter === LEAVE_STATUS.CANCELLATION_REQUESTED
          ? 'There are currently no leave cancellation requests waiting for your review.'
          : 'There are currently no leave requests waiting for your review.'}
      </Typography>
    </div>
  );

  return (
    <div className="leave-page-container">
      <PageHeader
        title="Leave Approval"
        left={
          <IconButton onClick={() => navigate(-1)} size="medium">
            <ArrowBackIcon />
          </IconButton>
        }
        right={
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      {SnackbarComponent}

      {/* ── Approve / Reject confirmation dialog ─────────────────────── */}
      <LeaveActionDialog
        open={Boolean(pendingAction)}
        status={pendingAction?.status}
        row={pendingAction?.row}
        comment={actionByComment}
        onCommentChange={setActionByComment}
        onConfirm={handleConfirmedAction}
        onClose={closeConfirm}
        fmtDate={fmtDate}
      />

      <Paper elevation={0} className="leave-card">
        {/* Header strip */}
        <div className="leave-card-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventAvailableIcon sx={{ color: '#fff', fontSize: 20 }} />
            <Typography className="leave-card-header-title" variant="subtitle1">
              Leave Approvals
            </Typography>
          </Box>

          {/* Type filter & Search box */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Select
              value={requestTypeFilter}
              onChange={(e) => {
                setRequestTypeFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              size="small"
              sx={{ minWidth: 190, height: 34, bgcolor: '#fff', borderRadius: 1 }}
            >
              {APPROVAL_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="leave-search-input"
            />
          </Box>
        </div>

        {/* Content */}
        {loading && leaves.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <TableSkeleton rows={4} columns={6} />
          </Box>
        ) : !loading && rowCount === 0 ? (
          renderEmptyState()
        ) : (
          <div className="leave-data-table">
            <div className="leave-data-grid">
              <DataGrid
                rows={leaves}
                columns={columns}
                getRowId={(row) => row.id}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 20]}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                loading={loading}
                disableRowSelectionOnClick
                sx={{
                  height: '100%',
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { bgcolor: 'rgba(4,88,100,0.04)' },
                  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                  '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(4,88,100,0.03)' },
                }}
              />
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default LeaveApproval;
